import React, { useState, useEffect, useRef } from 'react';
import { Link, Navigate } from 'react-router-dom';
import VendrLayout from '../VendrLayout';
import { useClientProfile } from '../context/ClientProfileContext';
import { useAmazonProducts } from '../context/AmazonProductsContext';
import { useAmazonRegistry } from '../context/AmazonBrandRegistryContext';
import {
  parseAndValidateAmazonImportFile,
  downloadAmazonTemplateXlsx,
  AMAZON_IMPORT_COLUMNS,
} from '../utils/amazonProductImport';
import {
  fetchActiveAmazonImportTemplate,
  downloadActiveTemplateToDisk,
} from '../../utils/productImportTemplateService';
import './VendrAmazonImportForm.css';

const PREVIEW_COLS = ['sku', 'product_name', 'brand_name', 'product_category', 'fulfillment_method', 'retail_price'];

const VendrAmazonImportForm = () => {
  const { isSetupComplete, loading: profileLoading } = useClientProfile();
  const { bulkImportProducts } = useAmazonProducts();
  const { isAmazonEnabled, isAmazonSubmitted, loading: amazonLoading } = useAmazonRegistry();

  const [activeTemplate, setActiveTemplate] = useState(null);
  const [templateChecked, setTemplateChecked] = useState(false);

  const [importFile, setImportFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);

  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchActiveAmazonImportTemplate().then(({ data }) => {
      setActiveTemplate(data || null);
      setTemplateChecked(true);
    });
  }, []);

  if (profileLoading || amazonLoading) {
    return (
      <VendrLayout>
        <div className="vaif-loading">Loading…</div>
      </VendrLayout>
    );
  }

  if (!isSetupComplete) return <Navigate to="/vendr/setup" replace />;
  if (isAmazonEnabled && !isAmazonSubmitted) return <Navigate to="/vendr/amazon-registry" replace />;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setParseResult(null);
    setImportResult(null);
    setImportError('');
    setParsing(true);
    const result = await parseAndValidateAmazonImportFile(file);
    setParsing(false);
    setParseResult(result);
  };

  const handleDownloadAdminTemplate = async () => {
    const result = await downloadActiveTemplateToDisk('amazon_products');
    if (!result.success) setImportError(result.error || 'Download failed');
  };

  const handleReset = () => {
    setImportFile(null);
    setParseResult(null);
    setImportResult(null);
    setImportError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleImport = async () => {
    if (!parseResult?.validRows?.length) return;
    setImporting(true);
    setImportError('');
    const result = await bulkImportProducts(parseResult.validRows);
    setImporting(false);
    if (!result.success) {
      setImportError(result.error || 'Import failed');
      return;
    }
    setImportResult(result);
  };

  const hasErrors = parseResult && parseResult.errors.length > 0;
  const hasValidRows = parseResult && parseResult.validRows.length > 0;

  return (
    <VendrLayout>
      <div className="vaif-page">
        <div className="vaif-header">
          <div>
            <h1>Import Amazon products</h1>
            <p className="vaif-subtitle">
              Upload a CSV or XLSX file to add multiple Amazon products at once.
              Images and documents must be added individually after import.
            </p>
          </div>
          <Link to="/vendr/products" className="vaif-back-link">← Back to products</Link>
        </div>

        {importResult ? (
          <div className="vaif-success-card">
            <div className="vaif-success-icon">✓</div>
            <h2>Import complete</h2>
            <p>
              <strong>{importResult.imported}</strong> Amazon product{importResult.imported !== 1 ? 's' : ''} imported successfully.
            </p>
            {importResult.errors.length > 0 && (
              <div className="vaif-import-errors">
                <p>{importResult.errors.length} row{importResult.errors.length !== 1 ? 's' : ''} could not be saved:</p>
                <ul>
                  {importResult.errors.map((e, i) => (
                    <li key={i}>Row {e.row}: {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="vaif-success-actions">
              <Link to="/vendr/products?tab=amazon" className="vaif-btn">View Amazon products</Link>
              <button type="button" className="vaif-btn vaif-btn-secondary" onClick={handleReset}>
                Import another file
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="vaif-import-note">
              <strong>Note:</strong> Spreadsheet import covers product data only. Required fields, images, and documents (e.g. main image, back of pack, SDS) must be added by editing each product after import.
            </div>

            <div className="vaif-card">
              <h2>Step 1 — Download a template</h2>
              <p className="vaif-card-desc">
                Start with a template to ensure your file has the correct column headers.
              </p>
              <div className="vaif-template-actions">
                <button type="button" className="vaif-btn" onClick={templateChecked && activeTemplate ? handleDownloadAdminTemplate : downloadAmazonTemplateXlsx}>
                  Download product template (.xlsx)
                </button>
              </div>
              <div className="vaif-columns-info">
                <p className="vaif-columns-title">Importable columns <span className="vaif-required-note">(* required)</span></p>
                <ul className="vaif-columns-list">
                  {AMAZON_IMPORT_COLUMNS.map((col) => (
                    <li key={col} className={['sku', 'product_name', 'brand_name', 'product_category'].includes(col) ? 'vaif-col-required' : ''}>
                      {col}
                      {['sku', 'product_name', 'brand_name', 'product_category'].includes(col) && ' *'}
                    </li>
                  ))}
                </ul>
                <p className="vaif-columns-note">
                  Image and document fields (main image, back of pack, SDS, etc.) are not importable via spreadsheet — add them individually after import.
                </p>
              </div>
            </div>

            <div className="vaif-card">
              <h2>Step 2 — Upload your file</h2>
              <p className="vaif-card-desc">Select a .csv or .xlsx file to validate and preview before importing.</p>
              <label className="vaif-file-label">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                  onChange={handleFileChange}
                  className="vaif-file-input"
                />
                <span className="vaif-file-btn">Choose file</span>
                <span className="vaif-file-name">{importFile ? importFile.name : 'No file chosen'}</span>
              </label>
              {parsing && <p className="vaif-parsing">Validating file…</p>}
            </div>

            {importError && (
              <div className="vaif-alert vaif-alert--error">{importError}</div>
            )}

            {parseResult && (
              <div className="vaif-card">
                <h2>Step 3 — Review and import</h2>

                {hasErrors && (
                  <div className="vaif-errors-section">
                    <p className="vaif-errors-title">
                      {parseResult.errors.length} validation error{parseResult.errors.length !== 1 ? 's' : ''} found
                      {hasValidRows ? ` — ${parseResult.validRows.length} row${parseResult.validRows.length !== 1 ? 's' : ''} are valid and can still be imported` : ''}
                    </p>
                    <ul className="vaif-errors-list">
                      {parseResult.errors.map((e, i) => (
                        <li key={i}>
                          <span className="vaif-error-row">Row {e.row}</span>
                          <span className="vaif-error-field">{e.field}</span>
                          <span className="vaif-error-msg">{e.error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {hasValidRows ? (
                  <>
                    <p className="vaif-preview-title">
                      Preview — {parseResult.validRows.length} valid row{parseResult.validRows.length !== 1 ? 's' : ''}
                    </p>
                    <div className="vaif-preview-wrap">
                      <table className="vaif-preview-table">
                        <thead>
                          <tr>
                            {PREVIEW_COLS.map((col) => <th key={col}>{col}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {parseResult.validRows.slice(0, 20).map((row, i) => (
                            <tr key={i}>
                              {PREVIEW_COLS.map((col) => (
                                <td key={col}>{row[col] != null ? String(row[col]) : '—'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {parseResult.validRows.length > 20 && (
                        <p className="vaif-preview-more">…and {parseResult.validRows.length - 20} more rows not shown.</p>
                      )}
                    </div>
                    <div className="vaif-import-actions">
                      <button
                        type="button"
                        className="vaif-btn vaif-btn-amazon"
                        onClick={handleImport}
                        disabled={importing}
                      >
                        {importing ? 'Importing…' : `Import ${parseResult.validRows.length} product${parseResult.validRows.length !== 1 ? 's' : ''}`}
                      </button>
                      <button type="button" className="vaif-btn vaif-btn-secondary" onClick={handleReset}>
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="vaif-no-valid">
                    <p>No valid rows to import. Fix the errors above and re-upload your file.</p>
                    <button type="button" className="vaif-btn vaif-btn-secondary" onClick={handleReset}>
                      Try again
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </VendrLayout>
  );
};

export default VendrAmazonImportForm;
